import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { switchRole } from '../api';
import { useLanguage } from './language-context';
import { useSignedInAccount } from './signed-in-account';
import { surfaceUrl, type Surface } from './surface-links';
import { useToast } from './toast';
import './account-menu-signout.css';

// The header's account control (design DC:162-176, rows DC:4953-4964; CB-17
// `account-menu.tsx`). Signed out it is the plain "Sign in" link; signed in it is the
// visitor's first name with a dropdown of customer-dashboard destinations.
//
// Every destination lives on another surface, so rows are real <a href> links built by
// surface-links.ts (tenant-blind: the origin is derived from the current host, never
// written here). Paths follow the PRD v2.5 customer route table (§7.3.2). The design's
// "Notifications" row is not carried: DR-38 retires that page in favour of the
// dashboard's top-bar tray, so the unread count shows on the tab bar's More badge.

interface Row {
  key: MessageKey;
  icon: string;
  path: string;
  badge?: number;
}

export function AccountMenu() {
  const { t, lang } = useLanguage();
  const { user, attention, signOut } = useSignedInAccount();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const signingOutRef = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!user) {
    return (
      <Link className="hdr__signin" to="/login">
        {t('nav.sign_in')}
      </Link>
    );
  }

  const firstName = (user.name ?? '').trim().split(/\s+/)[0] || t('account.menu');
  const rows: Row[] = [
    { key: 'account.overview', icon: '◈', path: '/' },
    { key: 'account.my_bookings', icon: '▦', path: '/bookings', badge: attention },
    { key: 'account.favourites', icon: '♥', path: '/saved' },
    { key: 'account.my_reviews', icon: '★', path: '/reviews' },
    { key: 'account.messages', icon: '✉', path: '/messages' },
    { key: 'account.profile_settings', icon: '◯', path: '/account' },
  ];
  const roles = user.roles ?? [];
  const switches: Array<{ key: MessageKey; role: 'merchant' | 'staff' | 'admin'; surface: Surface }> = [];
  if ((roles.includes('merchant') || roles.includes('staff')) && user.active_role !== 'merchant') {
    switches.push({ key: 'account.switch_business', role: roles.includes('merchant') ? 'merchant' : 'staff', surface: 'merchant' });
  }
  if (roles.includes('admin') && user.active_role !== 'admin') {
    switches.push({ key: 'account.switch_admin', role: 'admin', surface: 'admin' });
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => wrapRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus());
    }
  }

  async function onSwitch(role: 'merchant' | 'staff' | 'admin', surface: Surface) {
    setOpen(false);
    try {
      await switchRole(role);
      window.location.assign(surfaceUrl(surface, '/') ?? '/');
    } catch {
      toast.show(t('account.switch_failed'));
    }
  }

  // Sign-out feedback: the row stays open, disabled, with a spinner + "Signing out…"
  // until the call settles (a second click is ignored); then the menu closes and the
  // header falls back to "Sign in". A failed call still ends the local session — the
  // in-memory token is dropped either way (api.ts signOut).
  async function onSignOut() {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      /* best-effort — the local session is over regardless */
    } finally {
      signingOutRef.current = false;
      setSigningOut(false);
      setOpen(false);
    }
    toast.show(t('account.signed_out'));
  }

  return (
    <div className="acctmenu" ref={wrapRef} onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        className="acctmenu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('account.menu_for', { name: firstName })}
        onClick={() => setOpen((v) => !v)}
      >
        {firstName}
        <span className="acctmenu__caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="acctmenu__panel" role="menu" lang={lang}>
          {rows.map((r) => (
            <a key={r.key} role="menuitem" className="acctmenu__row" href={surfaceUrl('app', r.path) ?? r.path}>
              <span className="acctmenu__ico" aria-hidden="true">
                {r.icon}
              </span>
              {t(r.key)}
              {r.badge ? <span className="acctmenu__badge">{formatNumber(lang, r.badge)}</span> : null}
            </a>
          ))}
          {switches.map((s) => (
            <button key={s.key} type="button" role="menuitem" className="acctmenu__row" onClick={() => onSwitch(s.role, s.surface)}>
              <span className="acctmenu__ico" aria-hidden="true">
                ⇄
              </span>
              {t(s.key)}
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className="acctmenu__row"
            onClick={onSignOut}
            disabled={signingOut}
            aria-busy={signingOut}
          >
            <span className="acctmenu__ico" aria-hidden="true">
              {signingOut ? <span className="acctmenu__spinner" /> : '⏻'}
            </span>
            {t(signingOut ? 'common.signing_out' : 'account.sign_out')}
          </button>
        </div>
      )}
    </div>
  );
}
